import { Stack, Typography, Tabs, Tab } from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import PageSection from 'components/common/PageSection'
import { TabPanel } from 'components/common/TabPanel'
import { EditTestSuiteFormButtons } from 'components/test-suites/forms/EditTestSuiteFormButtons'
import { TestSuiteComposition } from 'components/test-suites/forms/test-suite-composition/TestSuiteComposition'
import { TestSuiteAddTeams } from 'components/test-suites/forms/TestSuiteAddTeams'
import { useTestSuiteFormContext } from 'components/test-suites/forms/TestSuiteFormContext'
import { TestSuiteName } from 'components/test-suites/forms/TestSuiteName'
import { TestSuiteRisks } from 'components/test-suites/forms/TestSuiteRisks'
import { TestSuiteUnderstandingScoreCalculation } from 'components/test-suites/forms/TestSuiteUnderstandingScoreCalculation'
import { getTestSuitesUrls } from 'components/test-suites/test-suites-urls-utils'
import { useGetTeams } from 'hooks/use-get-teams-paged'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'

enum Paths {
  AddTeams = 'test_suite.forms.edit_test_form.tab_teams',
  AddTests = 'test_suite.forms.edit_test_form.tab_tests',
  EditRisks = 'test_suite.forms.edit_test_form.tab_risks'
}

function a11yProps(id: string) {
  return {
    id: `tab-${id}`,
    'aria-controls': `tabpanel-${id}`
  }
}

export const EditTestSuiteForm: React.FC<{
  shouldShowWarning: boolean
}> = ({ shouldShowWarning }) => {
  const { canCreateAndEditTestSuites, testSuiteToEdit: testSuite } =
    useTestSuiteFormContext()

  const { tab, testSuiteId } = useParams<{
    tab: string
    testSuiteId: string
  }>()

  const { trans } = useTranslation()
  const testSuitesUrls = getTestSuitesUrls()

  const tabs = useMemo(() => {
    return [
      {
        id: 'teams',
        path: trans(Paths.AddTeams),
        value: 0
      },
      {
        id: 'tests',
        path: trans(Paths.AddTests),
        value: 1
      },
      {
        id: 'risks',
        path: trans(Paths.EditRisks),
        value: 2
      }
    ]
  }, [trans])

  const [tabValue, setTabValue] = useState(
    tab ? tabs.find((t) => t.path === tab)?.value ?? 0 : 0
  )
  const tabsRef = useRef(tabs)

  const navigate = useNavigate()

  const {
    data: teams,
    isFetching: isFetchingTeams,
    isError: hasErrorTeams
  } = useGetTeams()

  const location = useLocation()

  // update the tabValue when we detect a location change
  // we need to set the tabValue here as opposed to in handleTabChange so that
  // we can allow PromptDialog to potentially block the navigation first
  // and only update the tabValue after we have successfully allowed navigation
  useEffect(() => {
    const locationTabValue =
      tabsRef.current.find((tab) => location.pathname.includes(tab.path))
        ?.value ?? 0

    if (tabValue !== locationTabValue) {
      setTabValue(
        tabsRef.current.find((tab) => location.pathname.includes(tab.path))
          ?.value ?? 0
      )
    }
  }, [location, tabValue])

  const handleTabChange = (_, newValue) => {
    const path = tabs.find((tab) => tab.value === newValue).path
    navigate(testSuitesUrls.testSuiteFormTab(testSuiteId, path))
  }

  return (
    <PageSection
      errorMessage={
        <Typography>
          {trans('test_suites.errors.page_loading_error')}
        </Typography>
      }
      hasError={hasErrorTeams}
      isLoading={isFetchingTeams}
    >
      <Stack spacing={4}>
        <TestSuiteName />
        <TestSuiteUnderstandingScoreCalculation />
        <Tabs
          aria-label="edit-test-suite-tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'uppercase'
            }
          }}
          value={tabValue}
          onChange={handleTabChange}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.path}
              data-testid={`edit-test-suite-tab-${tab.id}`}
              label={tab.path}
              {...a11yProps(tab.id)}
            />
          ))}
        </Tabs>
        <TabPanel
          currentTabIndex={tabValue}
          tabIndex={0}
          tabPanelCss={{ padding: `0px ${theme.space[5]}px` }}
        >
          <TestSuiteAddTeams
            isDefaultTestSuite={Boolean(testSuite?.isDefault)}
            teams={teams}
          />
        </TabPanel>
        <TabPanel
          currentTabIndex={tabValue}
          tabIndex={1}
          tabPanelCss={{ padding: `0px ${theme.space[5]}px` }}
        >
          <TestSuiteComposition teams={teams} />
        </TabPanel>
        <TabPanel
          currentTabIndex={tabValue}
          tabIndex={2}
          tabPanelCss={{ padding: `0px ${theme.space[5]}px` }}
        >
          <TestSuiteRisks />
        </TabPanel>
        {canCreateAndEditTestSuites && (
          <EditTestSuiteFormButtons shouldShowWarning={shouldShowWarning} />
        )}
      </Stack>
    </PageSection>
  )
}
